import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from '../utils/toast';
import {
  ArrowLeft, Users, Plus, Calendar, MapPin, QrCode, CheckCircle2, Clock,
  XCircle, Lock, RotateCw, FileText, Mail, UserPlus, ExternalLink, Sparkles,
  Download, MoreVertical, Eye, Camera, Upload, ImagePlus, Trash2, X,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import DatePickerField from '../components/UI/DatePickerField';
import FormSelect from '../components/UI/FormSelect';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import SearchableCombobox from '../components/UI/SearchableCombobox';
import PillToggle from '../components/UI/PillToggle';
import { Pagination } from '../components/UI/Pagination';
import { CountBadge } from '../components/UI/CountBadge';
import { StatusBadge } from '../components/UI/StatusBadge';
import { InfoBadge } from '../components/UI/InfoBadge';
import ConfirmModal from '../components/UI/ConfirmModal';
import Portal from '../components/UI/Portal';
import { useClickOutside } from '../hooks/useClickOutside';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { formatDateTimeCompact, formatEventStartAt } from '../utils/dateUtils';
import { userCanHostAttendanceEvents } from '../utils/attendanceEventHostUtils';

const EVENT_TYPES = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'training', label: 'Training' },
  { value: 'briefing', label: 'Briefing' },
  { value: 'other', label: 'Other' },
];

// Card rows are tall on mobile — keep pages short so hosts can scan without
// endless scroll. Pagination only renders when totalPages > 1.
const EVENTS_LIST_PAGE_SIZE = 10;

// Lifecycle scopes mirror host jobs: open = still actionable; past = done/abandoned.
const EVENT_LIST_SCOPES = [
  { id: 'open', label: 'Open' },
  { id: 'past', label: 'Past' },
  { id: 'all', label: 'All' },
];
const EVENT_LIST_SCOPE_IDS = new Set(EVENT_LIST_SCOPES.map((s) => s.id));

/** Read list chrome from ?scope=&page=&q= (defaults: open / 1 / ''). */
const parseEventsListSearchParams = (searchParams) => {
  const scopeRaw = searchParams.get('scope');
  const scope = EVENT_LIST_SCOPE_IDS.has(scopeRaw) ? scopeRaw : 'open';
  const pageRaw = parseInt(searchParams.get('page') || '1', 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;
  const q = searchParams.get('q') || '';
  return { scope, page, q };
};

/** Build a clean query string — omit defaults so /self-service/events stays tidy. */
const buildEventsListSearch = ({ scope = 'open', page = 1, q = '' } = {}) => {
  const params = new URLSearchParams();
  if (scope && scope !== 'open') params.set('scope', scope);
  if (page && page > 1) params.set('page', String(page));
  const trimmed = (q || '').trim();
  if (trimmed) params.set('q', trimmed);
  const s = params.toString();
  return s ? `?${s}` : '';
};

/** Only accept in-app list returns (blocks open-redirect via location.state). */
const eventsListReturnPath = (from) => {
  if (typeof from !== 'string') return '/self-service/events';
  const pathname = from.split('?')[0];
  return pathname === '/self-service/events' ? from : '/self-service/events';
};

// Event type → InfoBadge color. Categorical, not workflow, so we use the
// DS-sanctioned `<InfoBadge>` component (DS §"InfoBadge" line 3140). Colours
// chosen for visual distinction; they don't carry semantic weight beyond
// "tells meetings apart from training at a glance".
const EVENT_TYPE_BADGE_COLOR = {
  meeting:  'blue',
  training: 'purple',
  briefing: 'orange',
  other:    'gray',
};

const eventTypeLabel = (type) =>
  EVENT_TYPES.find((t) => t.value === type)?.label || type;

// Global `closed` is warning-tier; attendance-event Closed is terminal-ish
// for host scan (Past scope). Mirror offence_closed / accident_closed.
const badgeStatusForEvent = (status) =>
  (status === 'closed' ? 'event_closed' : status);

// -------------------------------------------------------------------
// API helpers — thin wrappers over the shared axios instance
// -------------------------------------------------------------------
const eventsAPI = {
  list: (params = {}) => api.get('/attendance-events/events/', { params }).then(r => r.data),
  scopeCounts: () => api.get('/attendance-events/events/scope-counts/').then(r => r.data),
  get: (id) => api.get(`/attendance-events/events/${id}/`).then(r => r.data),
  create: (payload) => api.post('/attendance-events/events/', payload).then(r => r.data),
  update: (id, payload) => api.patch(`/attendance-events/events/${id}/`, payload).then(r => r.data),
  action: (id, verb, body = {}) => api.post(`/attendance-events/events/${id}/${verb}/`, body).then(r => r.data),
  qrToken: (id) => api.get(`/attendance-events/events/${id}/qr-token/`).then(r => r.data),
  attendees: (id, statusFilter) => {
    const q = statusFilter ? `?status=${statusFilter}` : '';
    return api.get(`/attendance-events/events/${id}/attendees/${q}`).then(r => r.data);
  },
  approve: (id, aid) => api.post(`/attendance-events/events/${id}/attendees/${aid}/approve/`).then(r => r.data),
  reject: (id, aid, reason) => api.post(`/attendance-events/events/${id}/attendees/${aid}/reject/`, { reason }).then(r => r.data),
  projects: (includeInactive = false) =>
    api.get(`/attendance-events/projects-lookup/${includeInactive ? '?include_inactive=true' : ''}`)
      .then(r => r.data?.results || []),
  // Returns the full axios response (not r.data) — the caller needs the
  // `content-disposition` header to pick up the server-provided filename.
  downloadPDF: (id, snapshotId) =>
    api.get(`/attendance-events/events/${id}/pdf/${snapshotId}/download/`, {
      responseType: 'blob',
    }),
  listPhotos: (id) =>
    api.get(`/attendance-events/events/${id}/photos/`).then(r => r.data),
  // FormData upload — sets multipart/form-data automatically. We send each
  // file as `images[]` (the backend accepts both `image` singular and
  // `images` plural) so a single call can deliver a multi-pick from the
  // gallery.
  uploadPhotos: (id, files, caption = '') => {
    const formData = new FormData();
    files.forEach((f) => formData.append('images', f));
    if (caption) formData.append('caption', caption);
    return api.post(`/attendance-events/events/${id}/photos/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deletePhoto: (id, photoId) =>
    api.delete(`/attendance-events/events/${id}/photos/${photoId}/`).then(r => r.data),
};

// -------------------------------------------------------------------
// Event-photo upload constants — kept in sync with backend views.py.
// Drift here is mostly cosmetic (the server is the source of truth) but
// matching the values lets us reject obviously-bad files without a round
// trip and word the helper text accurately.
// -------------------------------------------------------------------
const PHOTO_MAX_PER_EVENT = 50;
const PHOTO_MAX_BYTES = 12 * 1024 * 1024; // 12 MB
// `image/*` would also let through SVG/animated formats Pillow can't open.
// Stick to the same allow-list the backend enforces.
const PHOTO_ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const PHOTO_ACCEPT_ATTR = PHOTO_ALLOWED_MIME.join(',');

// Pull the filename out of a Content-Disposition header. Handles both the
// simple `filename="foo.pdf"` form and the RFC 5987 `filename*=UTF-8''foo.pdf`
// form. Returns `fallback` when nothing matches (e.g. CORS stripped the header,
// or the server didn't send one).
const filenameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;
  const starMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (starMatch) {
    try { return decodeURIComponent(starMatch[1].trim()); } catch { /* fall through */ }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
  return plainMatch ? plainMatch[1].trim() : fallback;
};

// Trigger a browser download for a Blob. Creates a detached <a>, clicks it,
// then revokes the object URL on the next tick so the download actually starts
// before we free the memory.
const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

// Sentinel value used in the combobox when the host picks "Other / not project-related".
// Coerced to `null` when sent to the API so the DB stores NULL.
const PROJECT_OTHER = '__OTHER__';

// -------------------------------------------------------------------
// Project combobox — reusable project picker backed by the
// /attendance-events/projects-lookup/ endpoint.
// -------------------------------------------------------------------
const ProjectCombobox = ({ value, onChange, disabled = false }) => {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['attendance-events', 'projects-lookup', includeInactive],
    queryFn: () => eventsAPI.projects(includeInactive),
    staleTime: 60_000,
  });

  // "Other" is injected as the first row so the host has a single, explicit
  // affordance to declare the event isn't tied to a project. In the wire
  // format we treat Other === null.
  const items = useMemo(() => (
    [{ project_code: PROJECT_OTHER, project_name: 'Other / not project-related' }, ...projects]
  ), [projects]);

  const displayValue = useMemo(() => {
    if (!value) return '';
    if (value === PROJECT_OTHER) return 'Other / not project-related';
    const match = projects.find((p) => p.project_code === value);
    return match ? `${match.project_code} — ${match.project_name}` : value;
  }, [value, projects]);

  return (
    <div className="space-y-2">
      <SearchableCombobox
        items={items}
        value={value || ''}
        onChange={onChange}
        placeholder={isLoading ? 'Loading projects…' : 'Search project name or code…'}
        searchPlaceholder="Type to search…"
        emptyLabel="No matching projects"
        disabled={disabled}
        loading={isLoading}
        variant="form"
        theme="employee"
        displayFn={(p) => (p.project_code === PROJECT_OTHER
          ? p.project_name
          : `${p.project_code} — ${p.project_name}`)}
        valueFn={(p) => p.project_code}
        displayValue={displayValue}
        searchFn={(p, term) => {
          if (p.project_code === PROJECT_OTHER) return true;
          return (
            p.project_name?.toLowerCase().includes(term)
            || p.project_code?.toLowerCase().includes(term)
            || (p.project_client_name || '').toLowerCase().includes(term)
          );
        }}
        renderItem={({ item, isSelected, isHighlighted, onSelect }) => {
          const isOther = item.project_code === PROJECT_OTHER;
          return (
            <button
              key={item.project_code}
              type="button"
              onClick={onSelect}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
              } ${isHighlighted ? 'bg-gray-100' : ''}`}
            >
              <div className="font-medium">{item.project_name}</div>
              {!isOther && (
                <div className="text-xs text-gray-500 font-mono flex items-center gap-2">
                  <span>{item.project_code}</span>
                  {item.project_status === 'inactive' && (
                    <span className="inline-block px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-semibold tracking-wider uppercase">
                      Inactive
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        }}
      />
      <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
          disabled={disabled}
          className="w-3.5 h-3.5 rounded border-gray-300 accent-blue-700 text-blue-700 focus-visible:ring-blue-500"
        />
        Show inactive projects
      </label>
    </div>
  );
};

// -------------------------------------------------------------------
// Empty state
// -------------------------------------------------------------------
const EmptyState = ({ onCreate, canHost = true }) => (
  <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 text-center">
    <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
      canHost ? 'bg-blue-100' : 'bg-gray-100'
    }`}>
      {canHost
        ? <Sparkles className="w-8 h-8 text-blue-800" />
        : <Lock className="w-8 h-8 text-gray-400" />}
    </div>
    <h3 className="text-subsection-header mb-2">
      {canHost ? 'No events yet' : 'No hosted events'}
    </h3>
    <p className="text-body mb-6 max-w-md mx-auto">
      {canHost
        ? 'Host a meeting or training with a unique rotating QR code. Attendees scan, sign, and a polished PDF arrives in your inbox when you close the event.'
        : 'Your account type cannot host meetings or trainings. Ask a staff host to create the event, then scan their QR code to check in.'}
    </p>
    {canHost && (
      <button
        type="button"
        onClick={onCreate}
        className="btn btn-primary"
      >
        <Plus className="w-4 h-4 mr-2" />
        Create your first event
      </button>
    )}
  </div>
);

// -------------------------------------------------------------------
// Create Event Form (modal-ish inline card)
// -------------------------------------------------------------------
const CreateEventForm = ({ onCreated, onCancel }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [eventType, setEventType] = useState('meeting');
  const [eventDate, setEventDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [allowExternal, setAllowExternal] = useState(true);
  const [projectCode, setProjectCode] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) => eventsAPI.create(payload),
    onSuccess: (event) => {
      toast.success('Event created');
      queryClient.invalidateQueries({ queryKey: ['attendance-events', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['attendance-events', 'scope-counts'] });
      if (onCreated) onCreated(event);
    },
    onError: (err) => {
      const data = err?.response?.data;
      // DRF permission denials use `{ detail: "..." }`; validation errors use field maps.
      const detail = data?.detail;
      const fieldMsg = data && typeof data === 'object' && !detail
        ? Object.values(data)[0]
        : null;
      const msg = detail
        || (typeof data === 'string' ? data : null)
        || fieldMsg
        || 'Failed to create event';
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    },
  });

  // Build start_at from the selected date + start time, and derive a generous
  // end_at at end-of-day (23:59). Hosts close the event manually via the
  // "Close & email PDF" action, so the end time is really a fallback window
  // for auto-close / schedule display — not a hard gate.
  const startAt = useMemo(() => {
    if (!eventDate || !startTime) return null;
    const d = new Date(eventDate);
    d.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
    return d;
  }, [eventDate, startTime]);

  const endAt = useMemo(() => {
    if (!eventDate) return null;
    const d = new Date(eventDate);
    d.setHours(23, 59, 0, 0);
    return d;
  }, [eventDate]);

  const canSubmit = title.trim() && startAt && endAt && startAt < endAt;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit || mutation.isLoading) return;
    // PROJECT_OTHER and empty both mean "not tied to a project" — send NULL.
    const project = (!projectCode || projectCode === PROJECT_OTHER) ? null : projectCode;
    mutation.mutate({
      event_type: eventType,
      title: title.trim(),
      description: description.trim(),
      location: location.trim(),
      project,
      start_at: startAt.toISOString(),
      end_at: endAt.toISOString(),
      allow_external: allowExternal,
      requires_signature: true, // always on per policy
      // Moderation queue removed from the UI — default to direct check-in so
      // hosts aren't blindsided by a pending queue they didn't opt into.
      external_moderation_required: false,
    });
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 md:p-6 space-y-6">
      <div>
        <label className="form-label">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Safety Time-Out — Project P0201"
          className="input"
          maxLength={200}
          required
        />
      </div>

      <div>
        <label className="form-label">Event type</label>
        <FormSelect
          options={EVENT_TYPES}
          value={eventType}
          onChange={setEventType}
          theme="employee"
        />
      </div>

      <div>
        <label className="form-label">Project (optional)</label>
        <ProjectCombobox value={projectCode} onChange={setProjectCode} />
        <p className="text-xs text-gray-500 mt-1.5">
          Pick a project if this event is tied to one — it appears on the attendance PDF,
          the reference number, and audit reports. Pick <strong>Other</strong> or leave blank
          for company-wide events. <span className="text-orange-700 font-medium">
            Project can only be changed while the event is in draft.
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="form-label">
            Date <span className="text-red-500">*</span>
          </label>
          <DatePickerField
            selected={eventDate}
            onChange={setEventDate}
            theme="employee"
            placeholderText="Select date"
            minDate={new Date()}
          />
        </div>
        <div>
          <label className="form-label">
            Start time <span className="text-red-500">*</span>
          </label>
          <DatePickerField
            selected={startTime}
            onChange={setStartTime}
            theme="employee"
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="h:mm aa"
            placeholderText="Select time"
          />
        </div>
      </div>

      <div>
        <label className="form-label">Location (optional)</label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g. Conference Room A / Project P0201 Site Office"
          className="input"
          maxLength={200}
        />
      </div>

      <div>
        <label className="form-label">Description (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Agenda, reference numbers, or notes that should appear on the attendance record."
          className="textarea"
        />
      </div>

      <div className="space-y-3 rounded-xl bg-gray-50 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={allowExternal}
            onChange={(e) => setAllowExternal(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-blue-700 text-blue-700 focus-visible:ring-blue-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">Allow external attendees</span>
            <span className="block text-xs text-gray-600 mt-0.5">
              Contractors, clients, auditors, etc. Disable for internal-only events.
            </span>
          </span>
        </label>

        <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-3">
          <div className="w-4 h-4 rounded-full bg-blue-700 mt-0.5 flex-shrink-0" />
          <span className="text-xs text-blue-900">
            <strong>Signature required</strong> — every attendee signs on their own device. This is
            mandatory for both meetings and training and cannot be disabled.
          </span>
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-outline w-full sm:w-auto"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit || mutation.isLoading}
          className="btn btn-primary w-full sm:w-auto"
        >
          {mutation.isLoading ? 'Creating…' : 'Create event'}
        </button>
      </div>
    </form>
  );
};

// -------------------------------------------------------------------
// Kebab action menu for list rows
// -------------------------------------------------------------------
// Exposes row-level actions (View details, Cancel event) without cluttering
// the card. Follows the DS compact dropdown pattern (DS §6747): `.icon-button`
// trigger + `absolute right-0 mt-2 … rounded-2xl shadow-lg border py-2 z-10`
// panel + `useClickOutside` for dismissal.
//
// The kebab is deliberately rendered as a sibling of (not inside) the row's
// <Link>. Nesting a <button> inside an <a> is invalid HTML and breaks native
// link semantics (middle-click, copy URL, etc.). See the list-row markup.
const EventRowMenu = ({ onView, onCancel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useClickOutside(() => setIsOpen(false), isOpen);

  const close = () => setIsOpen(false);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        aria-label="Event actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen((v) => !v);
        }}
        className="icon-button"
      >
        <MoreVertical className="w-5 h-5" />
      </button>
      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-lg border border-gray-200 py-2 z-20"
        >
          <button
            type="button"
            role="menuitem"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              close();
              onView();
            }}
            className="w-full inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
          >
            <Eye className="w-4 h-4 text-gray-500" /> View details
          </button>
          <div className="my-1 border-t border-gray-100" aria-hidden />
          <button
            type="button"
            role="menuitem"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              close();
              onCancel();
            }}
            className="w-full inline-flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
          >
            <XCircle className="w-4 h-4" /> Cancel event
          </button>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// List view
// -------------------------------------------------------------------
const EventsList = ({ onCreate, canHost = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cancelTarget, setCancelTarget] = useState(null);

  // List chrome lives in the URL so detail → Back restores scope/search/page
  // (DS worklist: "Back restores list filters, pagination, search").
  const { scope, page, q: searchTerm } = useMemo(
    () => parseEventsListSearchParams(searchParams),
    [searchParams],
  );

  const patchListParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const cur = parseEventsListSearchParams(prev);
      const next = { ...cur, ...patch };
      // Scope / search changes drop page unless the caller sets it explicitly.
      if (('scope' in patch || 'q' in patch) && !('page' in patch)) {
        next.page = 1;
      }
      return new URLSearchParams(
        buildEventsListSearch(next).replace(/^\?/, ''),
      );
    }, { replace: true });
  }, [setSearchParams]);

  const listReturnTo = `${location.pathname}${location.search}`;

  const listParams = useMemo(() => ({
    scope,
    page,
    page_size: EVENTS_LIST_PAGE_SIZE,
    ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
  }), [scope, page, searchTerm]);

  const { data: eventsPage, isLoading, isError, isFetching } = useQuery({
    queryKey: ['attendance-events', 'list', listParams],
    queryFn: () => eventsAPI.list(listParams),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const { data: scopeCounts } = useQuery({
    queryKey: ['attendance-events', 'scope-counts'],
    queryFn: eventsAPI.scopeCounts,
    staleTime: 30_000,
  });

  // Row-level cancel. Separate from the detail-page mutation because the
  // host can cancel from the list without drilling in. On success we
  // invalidate list + scope badges so the cancelled row leaves Open.
  const cancelMutation = useMutation({
    mutationFn: (id) => eventsAPI.action(id, 'cancel'),
    onSuccess: () => {
      toast.success('Event cancelled');
      queryClient.invalidateQueries({ queryKey: ['attendance-events', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['attendance-events', 'scope-counts'] });
      setCancelTarget(null);
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Failed to cancel'),
  });

  const rows = Array.isArray(eventsPage) ? eventsPage : eventsPage?.results || [];
  const totalCount = Array.isArray(eventsPage)
    ? eventsPage.length
    : (eventsPage?.count ?? rows.length);
  const totalPages = Math.max(1, Math.ceil(totalCount / EVENTS_LIST_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const inventoryTotal = scopeCounts?.all;

  const scopeOptions = EVENT_LIST_SCOPES.map((s) => ({
    ...s,
    count: scopeCounts?.[s.id] ?? 0,
  }));

  if (isLoading && !eventsPage) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-500">Loading events…</div>
    );
  }
  if (isError && !eventsPage) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-red-600">
        Could not load events. Try refreshing.
      </div>
    );
  }
  // Truly empty inventory (no events in any scope) → onboarding empty state.
  // Wait for scope-counts so we don't flash EmptyState while badges load.
  if (!isLoading && scopeCounts && inventoryTotal === 0 && !searchTerm.trim()) {
    return <EmptyState onCreate={onCreate} canHost={canHost} />;
  }

  const emptyCopy = () => {
    if (searchTerm.trim()) {
      return (
        <>
          No events match <span className="font-semibold text-gray-700">“{searchTerm}”</span>
          {scope !== 'all' ? ` in ${scope}` : ''}.
        </>
      );
    }
    if (scope === 'open') {
      return canHost
        ? 'No open events. Create one, or switch to Past to find closed meetings.'
        : 'No open events right now.';
    }
    if (scope === 'past') {
      return 'No past events yet.';
    }
    return 'No events found.';
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <DebouncedSearchInput
          value={searchTerm}
          onSearch={(next) => patchListParams({ q: next })}
          placeholder="Search by title, reference, project, or location…"
          theme="employee"
          className="w-full max-w-xl"
        />
        {canHost && (
          <button
            type="button"
            onClick={onCreate}
            className="btn btn-primary w-full sm:w-auto sm:flex-shrink-0"
          >
            <Plus className="w-4 h-4 mr-2" />
            New event
          </button>
        )}
      </div>

      {/* Count badges → sectionHeader layout (DS PillToggle decision rule). */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-sm text-gray-600 min-w-0 pt-1">
          {scope === 'open'
            ? 'Drafts and live check-ins'
            : scope === 'past'
              ? 'Closed, finalized, and cancelled'
              : 'Every event you can access'}
        </p>
        <PillToggle
          value={scope}
          onChange={(next) => patchListParams({ scope: next })}
          options={scopeOptions}
          theme="employee"
          variant="text"
          layout="sectionHeader"
          ariaLabel="Event lifecycle scope"
        />
      </div>

      {!rows.length ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-500 text-sm">
          {emptyCopy()}
        </div>
      ) : (
        <div className={`space-y-3 ${isFetching ? 'opacity-70 transition-opacity' : ''}`}>
          {rows.map((ev) => {
            // Only expose the kebab when there's actually an action beyond "view"
            // (which a row tap already does). For closed/finalized/cancelled
            // events, or events the current user doesn't host, the kebab would
            // be redundant — so we don't render it and keep the row compact.
            const canCancel = ev.is_mine && (ev.status === 'draft' || ev.status === 'active');
            const confirmedCount = ev.confirmed_count || 0;
            // Dead "0 attendees" steals scan space on Past rows; keep the hero
            // when turnout exists or the event is still live.
            const showAttendees = confirmedCount > 0 || ev.status === 'active';
            const projectCode = ev.project?.project_code;
            const projectName = ev.project?.project_name;
            return (
              <article key={ev.id} className="relative">
                <Link
                  to={`/self-service/events/${ev.id}`}
                  state={{ from: listReturnTo }}
                  className={`block bg-white border border-gray-200 rounded-xl p-4 md:p-5 hover:border-gray-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${canCancel ? 'pr-12' : ''}`}
                >
                  {/* Host scan order: title → when/where → type/ref.
                      Status (and optional attendance) sit on the trailing rail. */}
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <h3
                        className="text-base md:text-lg font-semibold text-gray-900 truncate"
                        title={ev.title}
                      >
                        {ev.title}
                      </h3>

                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                          {formatEventStartAt(ev.start_at)}
                        </span>
                        {projectCode && (
                          <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                            <span className="font-mono font-semibold text-blue-800 flex-shrink-0">
                              {projectCode}
                            </span>
                            {projectName && (
                              <span className="text-gray-600 truncate" title={projectName}>
                                · {projectName}
                              </span>
                            )}
                          </span>
                        )}
                        {ev.location && (
                          <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate" title={ev.location}>{ev.location}</span>
                          </span>
                        )}
                      </div>

                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <InfoBadge
                          color={EVENT_TYPE_BADGE_COLOR[ev.event_type] || 'gray'}
                          label={eventTypeLabel(ev.event_type)}
                        />
                        {ev.reference_no && (
                          <span
                            className="text-xs text-gray-500 font-mono tracking-wider"
                            title="Event reference number"
                          >
                            {ev.reference_no}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <StatusBadge status={badgeStatusForEvent(ev.status)} />
                      {showAttendees && (
                        <div className="flex flex-col items-end gap-0.5">
                          <div className="flex items-center gap-1.5 text-blue-800">
                            <Users className="w-4 h-4" />
                            <span className="text-xl font-bold tabular-nums">{confirmedCount}</span>
                          </div>
                          <span className="text-xs text-gray-500">attendees</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
                {/* Kebab lives OUTSIDE the <Link> — nesting a <button> in an <a>
                    is invalid HTML and breaks native link semantics. Absolute
                    positioning keeps the visual "corner of the card" affordance. */}
                {canCancel && (
                  <div className="absolute top-3 right-3 z-10">
                    <EventRowMenu
                      onView={() => navigate(`/self-service/events/${ev.id}`, { state: { from: listReturnTo } })}
                      onCancel={() => setCancelTarget(ev)}
                    />
                  </div>
                )}
              </article>
            );
          })}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(nextPage) => patchListParams({ page: nextPage })}
            totalCount={totalCount}
            pageSize={EVENTS_LIST_PAGE_SIZE}
            theme="employee"
            noun="events"
            className="rounded-2xl border border-gray-100"
          />
        </div>
      )}

      {/* Row-level cancel confirmation. Uses the same ConfirmModal component
          as the detail-page danger zone; the copy is state-aware based on
          confirmed_count so the host gets a proportional warning. */}
      {cancelTarget && (
        <ConfirmModal
          isOpen={Boolean(cancelTarget)}
          title="Cancel this event?"
          variant="danger"
          theme="employee"
          confirmLabel="Cancel event"
          cancelLabel="Keep event"
          isLoading={cancelMutation.isLoading}
          onCancel={() => setCancelTarget(null)}
          onConfirm={() => cancelMutation.mutate(cancelTarget.id)}
        >
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            {cancelTarget.status === 'draft' && (
              <p>
                This draft will be marked as <strong>cancelled</strong>. No reference
                number is allocated to drafts, so nothing is burned. This cannot be undone.
              </p>
            )}
            {cancelTarget.status === 'active' && (cancelTarget.confirmed_count || 0) === 0 && (
              <p>
                This event will stop accepting check-ins immediately and be marked as{' '}
                <strong>cancelled</strong>. This cannot be undone.
              </p>
            )}
            {cancelTarget.status === 'active' && (cancelTarget.confirmed_count || 0) > 0 && (
              <>
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800">
                  <strong>
                    {cancelTarget.confirmed_count}{' '}
                    {cancelTarget.confirmed_count === 1 ? 'person has' : 'people have'}
                  </strong>{' '}
                  already checked in.
                </div>
                <p>
                  Cancelling stops further check-ins. Existing records are kept for audit
                  but the event will be marked <strong>cancelled</strong>, which may be
                  misleading if the event actually happened.
                </p>
                <p className="text-gray-600">
                  If the event did take place, open it and use{' '}
                  <strong>Close &amp; email PDF</strong> instead — that generates the
                  attendance PDF and locks the final attendee list.
                </p>
              </>
            )}
            <p className="text-xs text-gray-500">
              Reference {cancelTarget.reference_no || '—'} · {cancelTarget.title}
            </p>
          </div>
        </ConfirmModal>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// Event Detail — attendees, approvals, lifecycle actions, PDF history
// -------------------------------------------------------------------
const AttendeeRow = ({ att, onApprove, onReject, mutating }) => {
  const isPending = att.status === 'pending_approval';
  const isRejected = att.status === 'rejected';
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3 border border-gray-200 rounded-xl">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-gray-900 truncate">{att.display_name}</p>
          {att.attendee_type === 'external' ? (
            <InfoBadge color="pink" label="External" />
          ) : (
            <InfoBadge color="blue" label="Internal" />
          )}
          {isPending && <StatusBadge status="pending" />}
          {isRejected && <StatusBadge status="rejected" />}
        </div>
        <p className="text-xs text-gray-600 mt-0.5 truncate">
          {att.affiliation || '—'}{att.designation ? ` · ${att.designation}` : ''}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {formatDateTimeCompact(att.signed_at)}
        </p>
      </div>
      {att.signature_url && (
        <img src={att.signature_url} alt="signature" className="h-10 w-28 object-contain bg-gray-50 rounded border border-gray-200 flex-shrink-0" />
      )}
      {isPending && (
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => onReject(att.id)}
            disabled={mutating}
            className="btn btn-sm btn-outline text-xs"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => onApprove(att.id)}
            disabled={mutating}
            className="btn btn-sm btn-success text-xs"
          >
            Approve
          </button>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// useAuthenticatedImageBlobs — load a list of API-protected image URLs
// into in-memory blob URLs that <img src=...> can render.
//
// Why this exists: the photos endpoint streams bytes from a Bearer-token
// gated action, but `<img src="/api/...">` is fired by the browser
// without our SPA's Authorization header attached, so it would 401 every
// time. Fetching through the shared `api` axios instance attaches the
// header, then we wrap the response in `URL.createObjectURL` so the
// browser can render it like any other src.
//
// We revoke object URLs on cleanup so this hook doesn't leak memory when
// the host scrolls through a large gallery — important on phones where
// the JS heap is small and abandoned blob refs can accumulate fast.
// Same pattern as `TransferDetailModal.TransactionPhotosSection` for
// consistency across the codebase.
// -------------------------------------------------------------------
const useAuthenticatedImageBlobs = (photos) => {
  const [blobUrls, setBlobUrls] = useState({});

  useEffect(() => {
    let cancelled = false;
    const created = [];

    photos.forEach((p) => {
      if (!p.image_url) return;
      // Skip if already loaded — avoids re-fetching when the parent
      // re-renders due to upload/delete invalidations.
      if (blobUrls[p.id]) return;
      api.get(p.image_url, { responseType: 'blob' })
        .then((res) => {
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          created.push(url);
          setBlobUrls((prev) => ({ ...prev, [p.id]: url }));
        })
        .catch(() => {
          // Quiet failure — the empty src will render the broken-image
          // placeholder which is enough signal for the host to retry by
          // reloading. Toasting one error per photo would be noisy if
          // the user is offline mid-scroll.
        });
    });

    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
    // We deliberately omit `blobUrls` from the dep list — including it
    // would cause an immediate re-run on every successful fetch (because
    // we just mutated the map), creating an infinite loop. The skip
    // check at the top of the loop keeps us idempotent without the dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  // When `photos` shrinks (a delete), revoke and drop the now-orphaned
  // blob URLs so the map doesn't grow indefinitely across the session.
  useEffect(() => {
    setBlobUrls((prev) => {
      const next = {};
      photos.forEach((p) => {
        if (prev[p.id]) next[p.id] = prev[p.id];
      });
      // Revoke URLs that fell out of `photos`.
      Object.entries(prev).forEach(([id, url]) => {
        if (!next[id]) URL.revokeObjectURL(url);
      });
      return next;
    });
  }, [photos]);

  return blobUrls;
};

// -------------------------------------------------------------------
// PhotoLightbox — fullscreen preview that lets the host inspect a photo
// at native resolution and delete it without losing context.
//
// Mental model: "I tapped that small thumbnail because I want to see the
// photo properly. I might also want to delete it if it's blurry." So we
// show the full image, the caption, and a clearly destructive trash
// affordance — without forcing a navigation away from the event detail.
// -------------------------------------------------------------------
const PhotoLightbox = ({ photo, photos, index, onClose, onDelete, onPrev, onNext, canDelete, isDeleting, blobUrl }) => {
  // ESC closes; arrow keys navigate. Standard gallery shortcuts that
  // experienced desktop users will reach for instinctively.
  useEffect(() => {
    if (!photo) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photo, onClose, onPrev, onNext]);

  if (!photo) return null;
  const total = photos.length;
  const positionLabel = total > 1 ? `${index + 1} / ${total}` : '';

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black/90 z-50 flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Event photo preview"
      >
        {/* Top bar — close + position. Kept minimal so the photo dominates. */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 text-white">
          <div className="text-xs sm:text-sm text-white/70 font-mono tracking-wider">
            {positionLabel}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="icon-button-dark"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Image area — tap outside the image to close (a familiar
            iOS Photos / web lightbox gesture). */}
        <button
          type="button"
          onClick={onClose}
          className="flex-1 flex items-center justify-center px-4 cursor-zoom-out"
          aria-label="Close preview"
        >
          {blobUrl ? (
            <img
              src={blobUrl}
              alt={photo.caption || 'Event photo'}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="text-white/60 text-sm">Loading…</div>
          )}
        </button>

        {/* Footer — caption + meta + delete. Contained width on desktop
            for readability; full-width on mobile. */}
        <div className="px-4 sm:px-6 py-4 text-white bg-black/40 backdrop-blur-sm">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              {photo.caption && (
                <p className="text-sm sm:text-base font-medium leading-snug">{photo.caption}</p>
              )}
              <p className="text-xs text-white/60 mt-1">
                {photo.uploaded_by_name || 'Unknown'}
                {' · '}
                {formatDateTimeCompact(photo.uploaded_at)}
              </p>
            </div>
            {canDelete && (
              <button
                type="button"
                onClick={() => onDelete(photo)}
                disabled={isDeleting}
                className="btn btn-danger btn-sm flex-shrink-0 self-start sm:self-auto disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                {isDeleting ? 'Deleting…' : 'Delete photo'}
              </button>
            )}
          </div>
        </div>

        {/* Prev/next click targets — only when more than one photo. */}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={onPrev}
              aria-label="Previous photo"
              className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={onNext}
              aria-label="Next photo"
              className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center"
            >
              ›
            </button>
          </>
        )}
      </div>
    </Portal>
  );
};

// -------------------------------------------------------------------
// EventPhotosCard — host-facing photo gallery with capture + upload.
//
// Mental model the layout is built around:
//   "I'm hosting the meeting now. I want to grab a quick photo so the
//    record on file isn't just a list of names — it shows the room, the
//    whiteboard, who turned up. One tap from here, the camera should
//    open. The next tap puts the photo on the record."
//
// Design decisions:
// - On mobile, the two primary actions (Take Photo, Upload) are shown
//   as TWO equally-sized buttons because the muscle-memory difference
//   between "use the camera now" vs "pick something I shot earlier" is
//   significant. A single button with a "..." menu would hide the camera
//   path behind a step — which is the path we most want to encourage.
// - On desktop, the primary mode is drag/drop or "click to choose" —
//   webcams are fiddly and rarely the source of a meeting photo. We
//   keep the camera button visible for hybrid devices but lower
//   prominence.
// - The empty state is *guidance*, not chrome. It hints at what's good
//   to capture (group shot, agenda board) so the host has ideas without
//   having to think.
// - Lifecycle gating mirrors the backend: writes lock at finalized /
//   cancelled, but reads always work so the official record keeps showing
//   the photos.
// -------------------------------------------------------------------
const EventPhotosCard = ({ event, isHost }) => {
  const eventId = event.id;
  const isMobile = useIsMobile();
  const qc = useQueryClient();
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Photos are fetched separately from the event detail so a long-running
  // detail page doesn't end up with stale thumbnails after the host
  // takes a new shot. Auto-refetch when the event is active so multi-host
  // scenarios (someone else uploading on another device) sync within a
  // few seconds.
  const { data: photos = [], isLoading } = useQuery({
    queryKey: ['attendance-events', eventId, 'photos'],
    queryFn: () => eventsAPI.listPhotos(eventId),
    enabled: !!eventId,
    refetchInterval: event.status === 'active' ? 10_000 : false,
  });

  // Fetch each photo's bytes through the authenticated axios instance and
  // wrap them as in-memory blob URLs the browser can put into `<img src>`.
  // See `useAuthenticatedImageBlobs` for the rationale.
  const photoBlobs = useAuthenticatedImageBlobs(photos);

  // Whether photos can be added at all. The backend is the source of
  // truth; we just gray out the buttons in the UI for matching states
  // so the host doesn't tap a button that errors out.
  const canUpload = isHost && (event.status === 'draft' || event.status === 'active' || event.status === 'closed');
  // Delete is allowed in the same window as upload (the host can cleanup
  // a bad shot until the event is finalized). HR/admins can still tidy up
  // afterwards via Django admin if a real PDPA concern surfaces.
  const canDelete = isHost && event.status !== 'finalized' && event.status !== 'cancelled';

  const uploadMutation = useMutation({
    mutationFn: (files) => eventsAPI.uploadPhotos(eventId, files, ''),
    onSuccess: () => {
      toast.success('Photo uploaded');
      qc.invalidateQueries({ queryKey: ['attendance-events', eventId, 'photos'] });
      qc.invalidateQueries({ queryKey: ['attendance-events', eventId] });
    },
    onError: (err) => {
      const msg = err?.response?.data?.error || 'Upload failed';
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (photoId) => eventsAPI.deletePhoto(eventId, photoId),
    onSuccess: () => {
      toast.success('Photo deleted');
      qc.invalidateQueries({ queryKey: ['attendance-events', eventId, 'photos'] });
      qc.invalidateQueries({ queryKey: ['attendance-events', eventId] });
      setLightboxIndex(null);
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Failed to delete'),
  });

  // Common entry point for both camera and gallery <input>: validate
  // sizes/types up front and surface friendly messages so the user knows
  // why a particular file got skipped before the round trip.
  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const remaining = PHOTO_MAX_PER_EVENT - photos.length;
    if (remaining <= 0) {
      toast.error(`This event already has the maximum of ${PHOTO_MAX_PER_EVENT} photos.`);
      return;
    }
    const all = Array.from(fileList);
    // Type allow-list — tolerate the empty content-type some Android
    // pickers send by checking the extension as a fallback.
    const validTypes = all.filter((f) => {
      if (PHOTO_ALLOWED_MIME.includes(f.type)) return true;
      const ext = (f.name || '').toLowerCase().split('.').pop();
      return ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(ext);
    });
    const sized = validTypes.filter((f) => f.size <= PHOTO_MAX_BYTES);
    if (validTypes.length < all.length) {
      toast.error('Some files were skipped (only JPEG, PNG, WebP, HEIC are accepted).');
    }
    if (sized.length < validTypes.length) {
      toast.error(`Some photos were over ${PHOTO_MAX_BYTES / (1024 * 1024)} MB and were skipped.`);
    }
    const toUpload = sized.slice(0, remaining);
    if (sized.length > remaining) {
      toast.error(`Only ${remaining} more photo(s) can fit in this event.`);
    }
    if (toUpload.length === 0) return;
    uploadMutation.mutate(toUpload);
  };

  const handleCameraInput = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };
  const handleGalleryInput = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrag = (e) => {
    if (!canUpload) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setIsDragging(true);
    else if (e.type === 'dragleave') setIsDragging(false);
  };
  const handleDrop = (e) => {
    if (!canUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer?.files);
  };

  const lightboxPhoto = lightboxIndex != null ? photos[lightboxIndex] : null;
  const showPrev = () => setLightboxIndex((i) => (i == null ? null : (i - 1 + photos.length) % photos.length));
  const showNext = () => setLightboxIndex((i) => (i == null ? null : (i + 1) % photos.length));

  const onConfirmDelete = (photo) => {
    if (!photo || !canDelete) return;
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    deleteMutation.mutate(photo.id);
  };

  // Hide the entire card for non-hosts when there are no photos to show
  // — there's no useful empty state to render for a viewer.
  if (!isHost && photos.length === 0 && !isLoading) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
        <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2 min-w-0 truncate">
          <Camera className="w-4 h-4 text-blue-700 flex-shrink-0" /> <span className="truncate">Event photos</span>
          {photos.length > 0 && <CountBadge count={photos.length} theme="info" className="flex-shrink-0" />}
        </h2>
        {canUpload && photos.length > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Re-upload affordance for when there are already photos.
                Two buttons mirror the empty-state actions, just smaller. */}
            <input
              ref={cameraInputRef}
              type="file"
              accept={PHOTO_ACCEPT_ATTR}
              capture="environment"
              className="hidden"
              onChange={handleCameraInput}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept={PHOTO_ACCEPT_ATTR}
              multiple
              className="hidden"
              onChange={handleGalleryInput}
            />
            {isMobile ? (
              /* Mobile header — both actions render as icon-only buttons.
                 Two reasons for the symmetry:
                 1. The "Event photos" section title + count badge already
                    provide all the discovery context the host needs to
                    interpret the camera icon. A wide "Take photo" pill
                    next to a tiny gallery icon looked unbalanced and
                    crowded the count badge.
                 2. The empty-state below still uses the big text buttons,
                    so first-time hosts get the explicit labels there.
                    This header path only fires when photos already exist
                    — at which point the host has already used the feature
                    once and doesn't need re-teaching.
                 The camera button keeps the soft blue tint to telegraph
                 "this is the primary action" without resorting to a full
                 filled-button that would dominate the row again. */
              <>
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploadMutation.isLoading}
                  className="icon-button bg-blue-100 text-blue-700 hover:bg-blue-200 hover:text-blue-800 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                  title="Take photo"
                  aria-label="Take photo"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={uploadMutation.isLoading}
                  className="icon-button"
                  title="Upload from device"
                  aria-label="Upload from device"
                >
                  <Upload className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                disabled={uploadMutation.isLoading}
                className="btn btn-primary btn-sm"
              >
                <ImagePlus className="w-4 h-4 mr-1.5" />
                Add photos
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-4 sm:p-5">
        {isLoading ? (
          <div className="text-sm text-gray-500 text-center py-4">Loading photos…</div>
        ) : photos.length === 0 ? (
          /* Empty state — different copy depending on whether the user can
             upload, so a non-uploading viewer gets a passive placeholder
             instead of a CTA they can't act on. canUpload handles both
             "is the host" and "is the event in a writable state". */
          canUpload ? (
            <EventPhotosEmptyState
              isMobile={isMobile}
              onCamera={() => cameraInputRef.current?.click()}
              onGallery={() => galleryInputRef.current?.click()}
              isUploading={uploadMutation.isLoading}
              isDragging={isDragging}
              onDrag={handleDrag}
              onDrop={handleDrop}
              cameraInputRef={cameraInputRef}
              galleryInputRef={galleryInputRef}
              onCameraInput={handleCameraInput}
              onGalleryInput={handleGalleryInput}
            />
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">
              No photos uploaded for this event.
            </div>
          )
        ) : (
          <>
            {/* Loading bar at the top of the grid when an upload is in
                flight — keeps the existing thumbnails visible (no jarring
                spinner replacement) while still acknowledging the upload. */}
            {uploadMutation.isLoading && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-blue-50 text-blue-800 text-xs flex items-center gap-2">
                <RotateCw className="w-3.5 h-3.5 animate-spin" />
                Uploading…
              </div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3">
              {photos.map((p, i) => {
                const blobUrl = photoBlobs[p.id];
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    aria-label={`View photo ${i + 1}${p.caption ? ` — ${p.caption}` : ''}`}
                  >
                    {blobUrl ? (
                      <img
                        src={blobUrl}
                        alt={p.caption || `Event photo ${i + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      // Shimmer placeholder while the blob loads — keeps
                      // the grid layout stable so additions don't reflow.
                      <div className="w-full h-full bg-gray-200 animate-pulse" />
                    )}
                    {/* Subtle hover/focus overlay with caption preview if any.
                        Always visible on mobile (hover doesn't apply) so the
                        caption isn't completely hidden on touch devices. */}
                    {p.caption && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] sm:text-xs px-2 py-1 truncate text-left">
                        {p.caption}
                      </div>
                    )}
                  </button>
                );
              })}
              {/* Add-more tile — visible only when uploads are still
                  allowed and there's room. Clickable to open the gallery
                  picker (the most common "add more" path; camera shots
                  are typically taken one at a time via the header button). */}
              {canUpload && photos.length < PHOTO_MAX_PER_EVENT && (
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-blue-700 flex flex-col items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  aria-label="Add more photos"
                >
                  <Plus className="w-6 h-6 sm:w-7 sm:h-7" />
                  <span className="text-[10px] sm:text-xs mt-1">Add more</span>
                </button>
              )}
            </div>
            {/* Helper line for the gallery — quietly tells the host the
                cap so they can pace themselves. Only shows when we're
                more than half-full so it doesn't clutter low-photo events. */}
            {canUpload && photos.length >= PHOTO_MAX_PER_EVENT / 2 && (
              <p className="text-xs text-gray-500 mt-3 text-right">
                {photos.length} / {PHOTO_MAX_PER_EVENT} photos
              </p>
            )}
          </>
        )}

        {/* Locked-state helper for finalized/cancelled — explains why the
            "Add" buttons are gone, so the host doesn't think the feature
            is broken. */}
        {isHost && (event.status === 'finalized' || event.status === 'cancelled') && (
          <p className="mt-3 text-[11px] text-gray-500 inline-flex items-center gap-1">
            <Lock className="w-3 h-3" />
            {event.status === 'finalized'
              ? 'Photos are locked — this event has been finalized.'
              : 'Photos are locked — this event was cancelled.'}
          </p>
        )}
      </div>

      <PhotoLightbox
        photo={lightboxPhoto}
        photos={photos}
        index={lightboxIndex ?? 0}
        blobUrl={lightboxPhoto ? photoBlobs[lightboxPhoto.id] : null}
        onClose={() => setLightboxIndex(null)}
        onPrev={showPrev}
        onNext={showNext}
        onDelete={onConfirmDelete}
        canDelete={canDelete}
        isDeleting={deleteMutation.isLoading}
      />
    </div>
  );
};

// -------------------------------------------------------------------
// EventPhotosEmptyState — the first thing a brand-new event shows when
// the host first lands on the detail page. The copy is the dominant UX
// element here: it tells the host *what kinds of things are worth
// capturing* (group shot, whiteboard, room) so they don't have to invent
// the answer themselves. The CTAs sit prominently below.
// -------------------------------------------------------------------
const EventPhotosEmptyState = ({
  isMobile, onCamera, onGallery, isUploading, isDragging, onDrag, onDrop,
  cameraInputRef, galleryInputRef, onCameraInput, onGalleryInput,
}) => (
  <div
    onDragEnter={onDrag}
    onDragLeave={onDrag}
    onDragOver={onDrag}
    onDrop={onDrop}
    className={`relative rounded-xl border-2 border-dashed p-5 sm:p-6 text-center transition-colors ${
      isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'
    }`}
  >
    {/* Hidden inputs — same refs the parent uses for the header buttons
        so the camera/gallery flow always lands here regardless of which
        affordance the host taps. */}
    <input
      ref={cameraInputRef}
      type="file"
      accept={PHOTO_ACCEPT_ATTR}
      capture="environment"
      className="hidden"
      onChange={onCameraInput}
    />
    <input
      ref={galleryInputRef}
      type="file"
      accept={PHOTO_ACCEPT_ATTR}
      multiple
      className="hidden"
      onChange={onGalleryInput}
    />

    <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
      <Camera className="w-6 h-6 text-blue-700" />
    </div>
    <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
      Capture this moment
    </h3>
    <p className="text-xs sm:text-sm text-gray-600 max-w-md mx-auto mb-4">
      Add photos of your event — a group shot of the room, the agenda
      board, or the project setup. Anything visual goes on file with the
      attendance record.
    </p>

    {/* CTAs — two equally-sized buttons on mobile so "use camera now" is
        as easy to reach as "pick from gallery". On desktop the gallery
        path is the default; the camera button is offered as a secondary
        action because hybrid devices with webcams are rare meeting hosts. */}
    <div className="flex flex-col sm:flex-row gap-2 sm:justify-center max-w-sm mx-auto">
      {isMobile ? (
        <>
          <button
            type="button"
            onClick={onCamera}
            disabled={isUploading}
            className="btn btn-primary flex-1 w-full sm:w-auto"
          >
            <Camera className="w-4 h-4 mr-2" />
            Take photo
          </button>
          <button
            type="button"
            onClick={onGallery}
            disabled={isUploading}
            className="btn btn-outline flex-1 w-full sm:w-auto"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onGallery}
            disabled={isUploading}
            className="btn btn-primary w-full sm:w-auto"
          >
            <ImagePlus className="w-4 h-4 mr-2" />
            Choose photos
          </button>
          <button
            type="button"
            onClick={onCamera}
            disabled={isUploading}
            className="btn btn-outline w-full sm:w-auto"
            title="Use device camera"
          >
            <Camera className="w-4 h-4 mr-2" />
            Take photo
          </button>
        </>
      )}
    </div>
    {!isMobile && (
      <p className="text-xs text-gray-500 mt-3">
        …or drag and drop image files anywhere on this card.
      </p>
    )}
    {isUploading && (
      <p className="text-xs text-blue-700 mt-3 inline-flex items-center gap-1.5">
        <RotateCw className="w-3 h-3 animate-spin" /> Uploading…
      </p>
    )}
  </div>
);

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  // Restore list chrome (scope/search/page) when the host drilled in from the list.
  const listBackTo = eventsListReturnPath(location.state?.from);

  const { data: event, isLoading } = useQuery({
    queryKey: ['attendance-events', eventId],
    queryFn: () => eventsAPI.get(eventId),
    enabled: !!eventId,
  });

  const { data: attendees, refetch: refetchAttendees } = useQuery({
    queryKey: ['attendance-events', eventId, 'attendees'],
    queryFn: () => eventsAPI.attendees(eventId),
    enabled: !!eventId,
    refetchInterval: event?.status === 'active' ? 5000 : false,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['attendance-events', eventId] });
    qc.invalidateQueries({ queryKey: ['attendance-events', 'list'] });
    qc.invalidateQueries({ queryKey: ['attendance-events', 'scope-counts'] });
    refetchAttendees();
  };

  const actionMutation = useMutation({
    mutationFn: ({ verb, body }) => eventsAPI.action(eventId, verb, body),
    onSuccess: (_data, vars) => {
      toast.success(`Event ${vars.verb}`);
      invalidate();
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Action failed'),
  });

  const approveMutation = useMutation({
    mutationFn: (aid) => eventsAPI.approve(eventId, aid),
    onSuccess: () => { toast.success('Approved'); invalidate(); },
    onError: (err) => toast.error(err?.response?.data?.error || 'Failed to approve'),
  });
  const rejectMutation = useMutation({
    mutationFn: ({ aid, reason }) => eventsAPI.reject(eventId, aid, reason),
    onSuccess: () => { toast.success('Rejected'); invalidate(); },
    onError: (err) => toast.error(err?.response?.data?.error || 'Failed to reject'),
  });

  // Per-row download state: tracks which snapshot is currently being fetched
  // so only that row shows the spinner. A single boolean would disable every
  // download button while any one is in flight — bad UX if a host wants to
  // grab v1 and v2 in quick succession.
  const [downloadingId, setDownloadingId] = useState(null);

  // Cancel-event confirmation dialog. The action itself is destructive but
  // non-recoverable (cancelled is a terminal state per the backend
  // transition rules), so we always gate it through ConfirmModal. Exposed
  // only when the event is DRAFT or ACTIVE — CLOSED events have real audit
  // records and must go through Reopen → new Close path if a change is
  // needed; they cannot be retroactively cancelled.
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleDownloadPDF = async (pdf) => {
    if (downloadingId) return;
    setDownloadingId(pdf.id);
    try {
      const res = await eventsAPI.downloadPDF(eventId, pdf.id);
      // Fallback name when the server didn't send a Content-Disposition
      // header. The backend uses `_FINAL` for `is_final=True` snapshots
      // (legacy finalized events created before the UI simplification);
      // for everything else we just use the version tag — the host can
      // distinguish the latest from older snapshots in their downloads
      // folder by mtime, and the version is the canonical disambiguator.
      const fallbackName = `${event.reference_no || eventId.slice(0, 8).toUpperCase()}_${pdf.is_final ? 'FINAL' : `v${pdf.version}`}.pdf`;
      const filename = filenameFromDisposition(
        res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'],
        fallbackName,
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      triggerBlobDownload(blob, filename);
    } catch (err) {
      // responseType:'blob' means the error body is a Blob; read it as text so
      // the server's JSON error message can reach the toast instead of '[object Blob]'.
      let message = 'Failed to download PDF';
      const errData = err?.response?.data;
      if (errData && typeof errData.text === 'function') {
        try {
          const txt = await errData.text();
          const parsed = JSON.parse(txt);
          message = parsed?.error || parsed?.detail || message;
        } catch { /* keep default */ }
      } else if (typeof errData?.error === 'string') {
        message = errData.error;
      }
      toast.error(message);
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading || !event) {
    return (
      <DashboardLayout>
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-500">Loading…</div>
      </DashboardLayout>
    );
  }

  const confirmed = (attendees || []).filter(a => a.status === 'confirmed');
  const pending = (attendees || []).filter(a => a.status === 'pending_approval');
  const rejected = (attendees || []).filter(a => a.status === 'rejected');

  const pdfSnapshots = event.pdf_snapshots || [];
  const isActive = event.status === 'active';
  const isClosed = event.status === 'closed';
  const isDraft = event.status === 'draft';
  // `finalized` is no longer reachable from the UI (the Finalize button
  // was removed) but the state still exists in the backend for any
  // historical events created before that change. We don't branch on it
  // anywhere in the host UI — those events render through the same
  // read-only path as `closed`.

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          {/* Title column + actions column — stacks on mobile, row on desktop.
              The back arrow lives inline with the status-chip row so the title
              and meta below start flush with the card's inner edge (no indent). */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between lg:gap-6">
            <div className="min-w-0 flex-1">
              {/* Chip row order: [back] → identity (type + reference) → state.
                  Puts the stable identifiers first (what this event IS) and the
                  lifecycle state last (what it currently is DOING), matching the
                  "PR #1234 — Title — Open" convention. */}
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <Link
                  to={listBackTo}
                  aria-label="Back to events"
                  className="icon-button -ml-2 sm:-ml-2.5 flex-shrink-0"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Link>
                <InfoBadge
                  color={EVENT_TYPE_BADGE_COLOR[event.event_type] || 'gray'}
                  label={eventTypeLabel(event.event_type)}
                />
                {event.reference_no && (
                  <span
                    className="text-sm text-gray-600 font-mono tracking-wider"
                    title="Event reference number"
                  >
                    {event.reference_no}
                  </span>
                )}
                <StatusBadge status={badgeStatusForEvent(event.status)} showIcon />
              </div>
              <h1 className="text-subsection-header">{event.title}</h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {formatEventStartAt(event.start_at)}
                </span>
                {event.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </span>
                )}
                {event.project?.project_code && (
                  <span className="inline-flex items-center gap-1.5 text-blue-700">
                    <span className="font-mono font-semibold">{event.project.project_code}</span>
                    <span className="text-gray-600">· {event.project.project_name}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Action bar — side-by-side on mobile (equal flex), right-aligned on desktop.
                Buttons use `flex-1 lg:flex-initial` so they share the row evenly on narrow
                screens and revert to intrinsic width on desktop. */}
            <div className="mt-4 lg:mt-0 flex gap-2 lg:flex-shrink-0 lg:justify-end">
              {isDraft && (
                <button
                  type="button"
                  onClick={() => actionMutation.mutate({ verb: 'activate' })}
                  disabled={actionMutation.isLoading}
                  className="btn btn-primary flex-1 lg:flex-initial whitespace-nowrap self-start lg:self-auto"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Activate & show QR
                </button>
              )}
              {isActive && (
                <>
                  <Link
                    to={`/self-service/events/${event.id}/present`}
                    className="btn btn-primary flex-1 lg:flex-initial whitespace-nowrap self-start lg:self-auto"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    Present QR
                  </Link>
                  <button
                    type="button"
                    onClick={() => actionMutation.mutate({ verb: 'close' })}
                    disabled={actionMutation.isLoading}
                    className="btn btn-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-1 lg:flex-initial whitespace-nowrap self-start lg:self-auto"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    <span className="sm:hidden">Close event</span>
                    <span className="hidden sm:inline">Close &amp; email PDF</span>
                  </button>
                </>
              )}
              {isClosed && (
                /* Closed state has a single escape valve: Reopen. We
                   deliberately do NOT expose a "Finalize / lock
                   permanently" button — closing already produces the
                   immutable PDF snapshot + audit trail that constitute
                   the official record. A second irreversible action on
                   top of that is decision fatigue without a real
                   workflow benefit; reopen-then-close is fully
                   audit-logged and that's the safety story. The
                   `finalize` backend endpoint stays in place as an
                   admin-only escape hatch but is not surfaced in the
                   host UI. */
                <button
                  type="button"
                  onClick={() => actionMutation.mutate({ verb: 'reopen' })}
                  disabled={actionMutation.isLoading}
                  className="btn btn-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-1 lg:flex-initial whitespace-nowrap"
                >
                  <RotateCw className="w-4 h-4 mr-2" />
                  <span className="sm:hidden">Reopen</span>
                  <span className="hidden sm:inline">Reopen for late check-ins</span>
                </button>
              )}
            </div>
          </div>

          {/* Project editing notice — spans full width below the header row */}
          {isDraft ? (
            <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
              <strong>Project can still be changed</strong> while this event is in draft.
              Once you activate the event, the project is locked to preserve reference-number
              integrity.
            </div>
          ) : (
            event.project?.project_code && (
              <p className="mt-3 text-[11px] text-gray-500 inline-flex items-center gap-1">
                <Lock className="w-3 h-3" /> Project locked — this event is no longer in draft.
              </p>
            )
          )}

          {/* Danger zone — cancel is deliberately placed below the primary
              action bar, separated by a divider, and sized smaller (btn-sm).
              This keeps it discoverable for the rare legitimate case (booking
              error, event won't happen) without visually competing with the
              primary lifecycle actions. Only rendered for DRAFT + ACTIVE; the
              backend refuses to transition CLOSED/FINALIZED → CANCELLED. */}
          {(isDraft || isActive) && event.is_mine && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-xs text-gray-500 leading-relaxed">
                {isDraft
                  ? 'Abandoning this draft? Cancelling keeps a record that it existed for auditability.'
                  : 'Event not going ahead? Cancelling stops further check-ins. If it did happen, use Close & email PDF instead.'}
              </p>
              <button
                type="button"
                onClick={() => setShowCancelModal(true)}
                disabled={actionMutation.isLoading}
                className="btn btn-danger btn-sm flex-shrink-0 self-start sm:self-auto"
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                Cancel event
              </button>
            </div>
          )}
        </div>

        {/* Event photos — full-width card sitting between the header and
            the attendee grid. Placement rationale:
            - It's a primary action while the event is live (host taps
              "Take photo" while standing in the room), so it needs to be
              above the fold on mobile, not buried below the attendee list.
            - But it sits below the lifecycle action bar so the host's
              first decision (Activate / Close / Reopen) is still the most
              prominent.
            - For non-host viewers with no photos, the card hides entirely
              (`EventPhotosCard` returns null) and we don't leave a gap. */}
        <EventPhotosCard event={event} isHost={!!event.is_mine} />

        {/* Attendee grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Confirmed */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
              <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2 min-w-0 truncate">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> <span className="truncate">Confirmed</span>
                <CountBadge count={confirmed.length} theme="success" className="flex-shrink-0" />
              </h2>
              {event.is_mine && (isActive || isDraft) && (
                <Link
                  to={`/self-service/events/${event.id}/present`}
                  title="Present QR (fullscreen)"
                  aria-label="Present QR (fullscreen)"
                  className="icon-button flex-shrink-0"
                >
                  <ExternalLink className="w-4 h-4" />
                </Link>
              )}
            </div>
            {confirmed.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                {isActive ? 'Waiting for the first check-in…' : 'No confirmed attendees.'}
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {confirmed.map(att => (
                  <AttendeeRow key={att.id} att={att} onApprove={() => {}} onReject={() => {}} />
                ))}
              </div>
            )}
          </div>

          {/* Pending + PDFs sidebar */}
          <div className="space-y-6">
            {pending.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-600" /> Pending approval
                    <CountBadge count={pending.length} theme="warning" />
                  </h2>
                </div>
                <div className="space-y-3 p-4">
                  {pending.map(att => (
                    <AttendeeRow
                      key={att.id}
                      att={att}
                      mutating={approveMutation.isLoading || rejectMutation.isLoading}
                      onApprove={(aid) => approveMutation.mutate(aid)}
                      onReject={(aid) => {
                        const reason = window.prompt('Reason for rejection (optional):') || '';
                        rejectMutation.mutate({ aid, reason });
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-800" /> Attendance records
                </h2>
              </div>
              {pdfSnapshots.length === 0 ? (
                <div className="p-5 text-sm text-gray-500">
                  No PDFs generated yet. One will be produced and emailed to you when you
                  <strong className="text-gray-700"> Close</strong> the event.
                </div>
              ) : (
                <ul className="space-y-3 p-4">
                  {/* Snapshots arrive newest-first (the model orders by
                      `-version`). With Finalize gone from the UI, the
                      newest snapshot IS the de facto official record —
                      so we label it "Latest record" (or just "Attendance
                      record" when there's only one) and call older
                      reopen-cycle snapshots "Version N" so the host can
                      see they were superseded but still grab them. */}
                  {pdfSnapshots.map((pdf, idx) => {
                    const isLatest = idx === 0;
                    const onlyOne = pdfSnapshots.length === 1;
                    const label = isLatest
                      ? (onlyOne ? 'Attendance record' : `Latest record (v${pdf.version})`)
                      : `Version ${pdf.version}`;
                    const ariaLabel = isLatest
                      ? 'Download latest attendance record'
                      : `Download attendance record version ${pdf.version}`;
                    const titleAttr = isLatest
                      ? 'Download latest record'
                      : `Download v${pdf.version}`;
                    return (
                      <li key={pdf.id} className="px-4 py-3 flex items-center justify-between gap-3 border border-gray-200 rounded-xl">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {label}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {formatDateTimeCompact(pdf.generated_at)}
                            {' · '}{pdf.attendee_count} attendees
                          </p>
                          {pdf.emailed_to && (
                            <p className="text-xs text-gray-400 mt-0.5 inline-flex items-center gap-1">
                              <Mail className="w-3 h-3" /> Emailed to {pdf.emailed_to}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDownloadPDF(pdf)}
                          disabled={downloadingId === pdf.id || !pdf.download_url}
                          className="icon-button flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={ariaLabel}
                          title={titleAttr}
                        >
                          {downloadingId === pdf.id
                            ? <RotateCw className="w-5 h-5 animate-spin" />
                            : <Download className="w-5 h-5" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {rejected.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-600" /> Rejected
                    <CountBadge count={rejected.length} theme="admin" />
                  </h2>
                </div>
                <div className="space-y-3 p-4">
                  {rejected.map(att => (
                    <AttendeeRow key={att.id} att={att} onApprove={() => {}} onReject={() => {}} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel-event confirmation. Copy is state-aware:
          - DRAFT: lightweight cancel, no audit fallout.
          - ACTIVE + 0 attendees: lightweight cancel.
          - ACTIVE + ≥1 attendee: hard warning — we surface the count and
            explicitly steer the host toward "Close & email PDF" because
            once people have checked in, the correct action is almost always
            to close the event, not cancel it. */}
      <ConfirmModal
        isOpen={showCancelModal}
        title="Cancel this event?"
        variant="danger"
        theme="employee"
        confirmLabel="Cancel event"
        cancelLabel="Keep event"
        isLoading={actionMutation.isLoading}
        onCancel={() => setShowCancelModal(false)}
        onConfirm={() => {
          actionMutation.mutate(
            { verb: 'cancel' },
            {
              onSuccess: () => setShowCancelModal(false),
            }
          );
        }}
      >
        <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
          {isDraft && (
            <p>
              This draft will be marked as <strong>cancelled</strong>. No reference
              number is allocated to drafts, so nothing is burned. This cannot be
              undone.
            </p>
          )}
          {isActive && (confirmed.length + pending.length) === 0 && (
            <p>
              This event will stop accepting check-ins immediately and be marked
              as <strong>cancelled</strong>. This cannot be undone.
            </p>
          )}
          {isActive && (confirmed.length + pending.length) > 0 && (
            <>
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800">
                <strong>
                  {confirmed.length + pending.length}{' '}
                  {confirmed.length + pending.length === 1 ? 'person has' : 'people have'}
                </strong>{' '}
                already checked in.
              </div>
              <p>
                Cancelling stops further check-ins. Existing records are kept for
                audit but the event will be marked <strong>cancelled</strong>,
                which may be misleading if the event actually happened.
              </p>
              <p className="text-gray-600">
                If the event did take place, use{' '}
                <strong>Close &amp; email PDF</strong> instead — that generates
                the attendance PDF and locks the final attendee list.
              </p>
            </>
          )}
          <p className="text-xs text-gray-500">
            Reference {event.reference_no || '—'} · {event.title}
          </p>
        </div>
      </ConfirmModal>
    </DashboardLayout>
  );
};

// -------------------------------------------------------------------
// Root routes
// -------------------------------------------------------------------
const SelfServiceEvents = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const canHost = userCanHostAttendanceEvents(user);

  const mode = useMemo(() => {
    // nested routing is done via App.js; this component is only used for /self-service/events
    return 'list';
  }, [location.pathname]);

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service"
              aria-label="Back to Self-Service"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="text-subsection-header">Meetings & Training</h1>
              <p className="text-body mt-1">
                {canHost
                  ? 'Host events and capture attendance with rotating QR codes.'
                  : 'View hosted events. Check in by scanning a host QR code.'}
              </p>
            </div>
          </div>
        </div>

        {creating && canHost ? (
          <CreateEventForm
            onCreated={(event) => {
              setCreating(false);
              navigate(`/self-service/events/${event.id}`);
            }}
            onCancel={() => setCreating(false)}
          />
        ) : (
          <EventsList
            canHost={canHost}
            onCreate={() => {
              if (canHost) setCreating(true);
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default SelfServiceEvents;
export { EventDetail };
